/*
 * Back-gesture support for the full-screen cart drawer.
 *
 * iOS Safari's left-to-right edge swipe is a browser navigation, not a DOM
 * event: it fires nothing we can preventDefault. The drawer is a same-document
 * overlay with no history entry of its own, so the gesture popped the entry
 * behind the PDP and unloaded the page — the shopper lost their place instead
 * of just closing the drawer. Fix: open() pushes a marked same-document entry
 * (same URL, no hash) for the gesture to pop, popstate closes the drawer, and
 * every other close path consumes that entry so no dead one is left behind.
 *
 * Module scope on purpose. global.js replaces <cart-drawer> wholesale while it
 * is open (global.js:1552 and :1702), so a listener or a flag bound to the
 * instance would die with the old element — the same reason BMScrollLock keys
 * its state on the document. The live drawer is resolved at event time.
 */
const BMDrawerHistory = window.__bmCartDrawerHistory || (window.__bmCartDrawerHistory = (function () {
  const KEY = 'bmCartDrawer';

  /* Set only while close() is running BECAUSE of a popstate, so close() knows
     the entry is already gone and must not pop a second one (which would take
     the shopper off the page). */
  let closingFromPop = false;
  /* Set between our own history.back() and the popstate it produces, so the
     handler ignores the event it caused itself, and so a second close() in the
     same tick cannot fire a second back() while history.state still shows the
     marker (history.state does not update until the popstate lands). */
  let selfPop = false;
  let selfPopTimer = null;

  const hasMarker = (state) => !!(state && state[KEY] === true);

  const liveDrawer = () => document.querySelector('cart-drawer');

  /* 'animate' is added on first open and never removed, and 'active' is two
     rAF frames behind open() (startOpenAnimation), so neither alone is a
     reliable "is open". The pending rAF handles on the live element cover
     exactly that opening window with no second source of truth to desync. */
  const isOpen = (drawer) => !!drawer && (
    drawer.classList.contains('active') ||
    drawer.openFrame != null ||
    drawer.openFrameSecond != null
  );

  const clearSelfPop = () => {
    selfPop = false;
    window.clearTimeout(selfPopTimer);
    selfPopTimer = null;
  };

  const api = {
    get closingFromPop() {
      return closingFromPop;
    },

    push() {
      /* Idempotent on history.state, never on a class or a flag: open() runs up
         to three times per Launch Bundle add (benchmark-pdp.js:703/713/764),
         again from product-form.js:972 and again from renderContents, and
         'active' is not set yet on the first of those calls. One entry only. */
      if (hasMarker(history.state)) return;
      try {
        history.pushState({ ...(history.state || {}), [KEY]: true }, '', location.href);
      } catch (e) { /* history is unavailable (sandboxed frame): drawer still works */ }
    },

    pop() {
      /* Never call back() on faith. global.js:501/2476 and benchmark-pdp.js:979
         replaceState on this entry and can wipe the marker; a back() without it
         would navigate the shopper away instead of closing the drawer. */
      if (selfPop) return;
      if (!hasMarker(history.state)) return;
      selfPop = true;
      /* Safety net: if the popstate never lands, a stuck flag would swallow the
         shopper's next real Back. */
      selfPopTimer = window.setTimeout(clearSelfPop, 800);
      try {
        history.back();
      } catch (e) {
        clearSelfPop();
      }
    },

    /* A fresh (non-bfcache) load that lands on the pushed entry — Back from
       checkout after the PDP was evicted — carries the marker with no drawer
       open. Strip it so the next Back is not silently swallowed. When the
       drawer IS open (bfcache restore, heap and DOM intact) leave everything
       alone: BMScrollLock.sanitize keeps the lock for the same reason, and the
       next swipe pops the marker into close() which releases it. */
    sanitize() {
      if (!hasMarker(history.state)) return;
      if (isOpen(liveDrawer())) return;
      const state = { ...history.state };
      delete state[KEY];
      try {
        history.replaceState(state, '', location.href);
      } catch (e) { /* see push() */ }
    },
  };

  window.addEventListener('popstate', (event) => {
    if (selfPop) {
      clearSelfPop();
      return;
    }
    if (hasMarker(event.state)) return;
    const drawer = liveDrawer();
    if (!isOpen(drawer)) return;
    closingFromPop = true;
    try {
      drawer.close();
    } finally {
      closingFromPop = false;
    }
  });

  window.addEventListener('pageshow', () => api.sanitize());
  api.sanitize();

  return api;
})());

/* GAH-QUICKWINS-01 FEEDBACK-1: Check out is a native form POST (/cart ->
   /checkout -> shop.app -> checkout), a second or more with nothing on screen
   but the browser's progress line - in the FB/IG webviews, a hairline. So the
   button that actually submitted is marked on the click frame: a white ring,
   and pointer-events off so a second tap cannot restart the navigation.
   Mark only, never block: nothing here cancels a submit, a submit a later
   listener cancels gets its button back in the next task, and the mark clears
   itself after 10s or when Back restores the page from bfcache.
   Its own class, not cart-control-loading: cart.js clearCartMutationLoading
   and the shipping-protection guard strip that one whenever a write settles.
   Own IIFE with a window guard, no top-level names: this is a classic script
   sharing one global lexical scope with every other one. */
(() => {
  if (window.__gdCheckoutLeaving) return;
  window.__gdCheckoutLeaving = true;
  const LEAVING = 'is-leaving-for-checkout';
  let timer = null;

  const clearLeaving = () => {
    window.clearTimeout(timer);
    timer = null;
    document.querySelectorAll(`.${LEAVING}`).forEach((el) => {
      el.classList.remove(LEAVING);
      el.removeAttribute('aria-busy');
    });
  };

  /* Also called by shipping-protection.js: its held tap leaves through
     form.submit() or location.href, neither of which fires a submit event. */
  window.markCheckoutLeaving = (el) => {
    if (!el || !el.classList) return;
    el.classList.add(LEAVING);
    el.setAttribute('aria-busy', 'true');
    window.clearTimeout(timer);
    timer = window.setTimeout(clearLeaving, 10000);
  };

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (event.defaultPrevented || !submitter || submitter.name !== 'checkout') return;
    if (!(event.target instanceof HTMLFormElement)) return;
    window.markCheckoutLeaving(submitter);
    // a listener that runs after this one can still cancel the submit
    window.setTimeout(() => {
      if (event.defaultPrevented) clearLeaving();
    }, 0);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) clearLeaving();
  });
})();

/* Daniel (Sep 7): the drawer's Upgrade card must never sit cut off behind the
   sticky footer. Measured after every render and on resize: if the scroll
   area overflows with the full card in it, the card drops to its compact
   view (no thumbnails, tighter rows); if the full card fits, it stays full.
   Module scope for the same reason as BMDrawerHistory - global.js swaps the
   <cart-drawer> element wholesale. */
/* Paul (10-18): the Upgrade button is never hidden on a phone. If it is
   still under the footer at the mini size, the list scrolls just far enough
   to show it. The scroll only runs after a render (reveal), never on
   resize, so the shopper's own scrolling is left alone. bmb-tight marks
   that state on the drawer for any style that wants it. */
const bmbFitUpgrade = (reveal) => {
  const drawer = document.querySelector('cart-drawer');
  const content = drawer && drawer.querySelector('.drawer__content');
  const card = content && content.querySelector('.bmb-upgrade');
  if (drawer) drawer.classList.remove('bmb-tight');
  if (content) bmbFitWatch(content);
  if (content && card && !content.clientHeight) {
    // not laid out yet (the desktop drawer is still sliding in): the
    // ResizeObserver below finishes the job once it has a height
    if (reveal === true) bmbFitPendingReveal = true;
    return;
  }
  if (!content || !card) return;
  const overflows = () => content.scrollHeight > content.clientHeight + 1;
  card.classList.remove('bmb-upgrade--compact', 'bmb-upgrade--mini');
  if (!overflows()) return;
  card.classList.add('bmb-upgrade--compact');
  if (!overflows()) return;
  // still cut off (a short phone with two lines in the cart): smaller again
  card.classList.add('bmb-upgrade--mini');
  if (!overflows()) return;
  drawer.classList.add('bmb-tight');
  if (reveal !== true) return;
  const button = card.querySelector('.bmb-upgrade__button') || card;
  const hidden = button.getBoundingClientRect().bottom - content.getBoundingClientRect().bottom;
  if (hidden > 0) content.scrollTop += hidden + 12;
};
const bmbFitUpgradeSoon = () => {
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => bmbFitUpgrade(true)));
};
/* Paul (10-18, desktop): a fit that ran before the drawer had its height
   bailed and nothing ran it again, so the full-size card sat with its
   button under the footer. Watching the list's own box re-fits whenever it
   gets or changes its height, whatever opened the drawer. A reveal that was
   skipped for want of a height is carried over; other size changes (a
   window resize) never scroll. */
let bmbFitPendingReveal = false;
let bmbFitWatched = null;
let bmbFitObserver = null;
let bmbFitObserveTimer = null;
const bmbFitWatch = (content) => {
  if (content === bmbFitWatched || typeof ResizeObserver !== 'function') return;
  if (!bmbFitObserver) {
    bmbFitObserver = new ResizeObserver(() => {
      window.clearTimeout(bmbFitObserveTimer);
      bmbFitObserveTimer = window.setTimeout(() => {
        const reveal = bmbFitPendingReveal;
        bmbFitPendingReveal = false;
        bmbFitUpgrade(reveal);
      }, 60);
    });
  }
  if (bmbFitWatched) bmbFitObserver.unobserve(bmbFitWatched);
  bmbFitWatched = content;
  bmbFitObserver.observe(content);
};
let bmbFitResizeTimer = null;
window.addEventListener('resize', () => {
  window.clearTimeout(bmbFitResizeTimer);
  bmbFitResizeTimer = window.setTimeout(bmbFitUpgrade, 120);
});

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initUpsellProductsSlider();
    this.reorderGwpItems();
    document.addEventListener("init:upsell-slider", this.initUpsellProductsSlider);
    this.initCartDrawerDefaultState();
    this.addEventListener('click', this.handleUltimateUpgradeClick.bind(this));
  }

  setHeaderCartIconAccessibility() {
    // every header marks its own cart link with js-cart-drawer-trigger, so this
    // needs no per-header knowledge and tolerates a page with no trigger at all
    document.querySelectorAll('.js-cart-drawer-trigger').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      // global.js replaces <cart-drawer> wholesale on some cart updates, which
      // re-runs this constructor. Bind once and resolve the live drawer at click
      // time, or every replaced drawer stays reachable through a stale closure.
      if (cartLink.dataset.cartDrawerBound) return;
      cartLink.dataset.cartDrawerBound = 'true';

      const openDrawer = (event) => {
        event.preventDefault();
        document.querySelector('cart-drawer')?.open(cartLink);
      };

      cartLink.addEventListener('click', openDrawer);
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') openDrawer(event);
      });
    });
  }

  initCartDrawerDefaultState() {
    const sectionIds = this.getSectionsToRender().map(s => s.section || s.id);
    // the instant cart's prototypes ride this request, so they are fresh on
    // every page without a request of their own (cart-instant.js)
    const ic = window.InstantCart;
    const protoId = ic && typeof ic.protoSection === 'function' ? ic.protoSection() : null;
    const base = (ic && typeof ic.protoPath === 'function' && ic.protoPath()) || '/';
    fetch(`${base}?sections=${[...sectionIds, ...(protoId ? [protoId] : [])].join(',')}&v=${new Date().getTime()}`)
      .then(r => r.json())
      .then(data => {
        if (protoId && data[protoId]) {
          ic.ingest(data[protoId]);
          delete data[protoId];
        }
        // Never re-render under an open drawer: this fetch races the user, and
        // its payload has no `items` array, so smoothSectionRender falls back to
        // wholesale-replacing .drawer__inner — destroying the checkout button
        // mid-tap and resetting scroll. An open drawer was opened from live
        // optimistic state; the SSR refresh only matters for the closed-drawer
        // first-open paint.
        if (this.classList.contains('active') || this.classList.contains('is-closing')) return;
        this.renderContents({sections: data}, 'default');
        const items = this.querySelector('cart-drawer-items');
        if (items) items.smoothSectionRender({sections: data});
        /* those renders can swap the whole drawer panel, taking the shipping
           protection widget the init sync already injected with it - put it
           back, sized to the real cart, so an existing cart shows it on the
           first open, not only after the next change */
        if (window.ShippingProtection) {
          try {
            ShippingProtection.ensureWidget();
            if (window._cartState) {
              ShippingProtection.updateWidgetVisibility(window._cartState);
              ShippingProtection.updateWidgetPrice();
            }
          } catch (e) {}
        }
        // Re-broadcast current cart state so any upsell-products elements newly
        // created by the renders above re-evaluate their slide visibility.
        if (window._cartState) {
          document.dispatchEvent(new CustomEvent('cart:state-updated', {
            detail: { cart: window._cartState }
          }));
        }
      })
      .catch(() => {});
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const wasActive = this.classList.contains('active');

    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.focusTrapTimer);
    this.cancelOpenAnimation();
    this.removeCloseTransitionListener();
    this.classList.remove('is-closing');
    this.lockBodyScroll();
    // Gives the iOS edge swipe something to pop instead of the previous page.
    // Idempotent, so the three open() calls per Launch Bundle add make one entry.
    BMDrawerHistory.push();
    this.startOpenAnimation();

    if (!wasActive) this.trapFocusAfterOpen();
    document.dispatchEvent(new CustomEvent('cart-drawer:show'));
    // the open transition is what gives .drawer__content its height
    bmbFitUpgradeSoon();
    window.setTimeout(() => bmbFitUpgrade(true), 400);
  }

  close() {
    // Consume the entry open() pushed, so X / overlay / Escape / Continue
    // shopping leave no dead history entry for the next Back to swallow. Skipped
    // when a popstate is what called us: that entry is already gone. Does not
    // touch the BMScrollLock pairing — every path still unlocks once in
    // finishClose, including this one.
    if (!BMDrawerHistory.closingFromPop) BMDrawerHistory.pop();

    const drawerInner = this.querySelector('.drawer__inner');
    const wasActive = this.classList.contains('active');

    window.clearTimeout(this.focusTrapTimer);
    this.cancelOpenAnimation();
    this.removeOpenTransitionListener();
    this.classList.remove('active');
    if (wasActive) this.classList.add('is-closing');
    removeTrapFocus();

    const finishClose = () => {
      window.clearTimeout(this.closeTimer);
      this.removeCloseTransitionListener();
      this.classList.remove('is-closing');
      this.unlockBodyScroll();

      // Re-focus the trigger element without scrolling
      if (this.activeElement) this.activeElement.focus({ preventScroll: true });
    };

    if (!wasActive || !drawerInner) {
      finishClose();
      return;
    }

    this._onCloseTransitionEnd = (event) => {
      if (event.target !== drawerInner || event.propertyName !== 'transform') return;
      finishClose();
    };
    drawerInner.addEventListener('transitionend', this._onCloseTransitionEnd);
    this.closeTimer = window.setTimeout(finishClose, 620);
  }

  startOpenAnimation() {
    this.classList.add('animate');
    this.openFrame = window.requestAnimationFrame(() => {
      this.openFrame = null;
      this.openFrameSecond = window.requestAnimationFrame(() => {
        this.openFrameSecond = null;
        this.classList.add('active');
      });
    });
  }

  cancelOpenAnimation() {
    if (this.openFrame) {
      window.cancelAnimationFrame(this.openFrame);
      this.openFrame = null;
    }
    if (this.openFrameSecond) {
      window.cancelAnimationFrame(this.openFrameSecond);
      this.openFrameSecond = null;
    }
  }

  trapFocusAfterOpen() {
    const drawerInner = this.querySelector('.drawer__inner');
    const focusDrawer = () => {
      // On touch devices the delayed programmatic focus() can scroll
      // .drawer__inner and swallow the tap the user already has in flight;
      // the focus trap is a keyboard nicety, so skip it for coarse pointers.
      if (window.matchMedia('(pointer: coarse)').matches) return;
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    if (!drawerInner) {
      focusDrawer();
      return;
    }

    this._onOpenTransitionEnd = (event) => {
      if (event.target !== drawerInner || event.propertyName !== 'transform') return;
      window.clearTimeout(this.focusTrapTimer);
      this.removeOpenTransitionListener();
      focusDrawer();
    };
    drawerInner.addEventListener('transitionend', this._onOpenTransitionEnd);

    this.focusTrapTimer = window.setTimeout(() => {
      this.removeOpenTransitionListener();
      focusDrawer();
    }, 700);
  }

  removeOpenTransitionListener() {
    if (!this._onOpenTransitionEnd) return;
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.removeEventListener('transitionend', this._onOpenTransitionEnd);
    this._onOpenTransitionEnd = null;
  }

  removeCloseTransitionListener() {
    if (!this._onCloseTransitionEnd) return;
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.removeEventListener('transitionend', this._onCloseTransitionEnd);
    this._onCloseTransitionEnd = null;
  }

  lockBodyScroll() {
    // The shared lock keys its state on the document (not this element), so it
    // survives global.js's wholesale <cart-drawer> replaceWith — the replacement
    // drawer's close() releases the same lock instead of orphaning a frozen
    // position:fixed body (the "header detached / page stuck" state).
    if (window.BMScrollLock) {
      BMScrollLock.lock('cart-drawer');
      return;
    }

    /* no-shared-script fallback (benchmark-scroll-lock.js failed to load) */
    if (this._bodyScrollLockedByCart) return;
    if (document.body.style.position === 'fixed') {
      // a previous (replaced) drawer instance left its lock behind — adopt it
      // so this instance's unlock releases it, instead of stranding the page
      this._bodyScrollLockedByCart = true;
      this._bodyScrollLockMode = 'fixed';
      this._scrollY = Math.abs(parseInt(document.body.style.top, 10)) || 0;
      return;
    }

    this._bodyScrollLockedByCart = true;
    this._scrollY = window.scrollY;
    this._bodyScrollLockMode = window.matchMedia('(max-width: 991px)').matches ? 'fixed' : 'overflow';
    if (this._bodyScrollLockMode !== 'fixed') return;

    // no body overflow:hidden here either - with html{overflow:hidden} it
    // would make <body> a scroll container and park the sticky header (and the
    // hamburger) off the top of the screen; see benchmark-scroll-lock.js
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockBodyScroll() {
    if (window.BMScrollLock) {
      BMScrollLock.unlock('cart-drawer');
      return;
    }

    /* no-shared-script fallback */
    if (!this._bodyScrollLockedByCart) return;

    if (this._bodyScrollLockMode === 'fixed') {
      document.documentElement.style.overflow = '';
      document.body.classList.remove('overflow-hidden');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';

      // Safety net: restore scroll in case the browser lost it
      window.scrollTo({ top: this._scrollY || 0, left: 0, behavior: 'instant' });
    }

    this._bodyScrollLockMode = null;
    this._bodyScrollLockedByCart = false;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, formType) {
    if (formType !== 'add-to-cart-form' && formType !== "default") {
      this.open();
    }

    if (formType === 'default' && (
      (window._cartState && isCartEffectivelyEmpty(window._cartState)) ||
      (window._CART_REQUESTS > 0) ||
      window._CART_DELETION_PENDING
    )) return;

    if (formType !== 'add-to-cart-form' && formType !== 'default' &&
        window._cartState && isCartEffectivelyEmpty(window._cartState) &&
        isCartEffectivelyEmpty(parsedState)) return;

    const drawerErrors = this.querySelector('#CartDrawer-CartErrors');
    if (drawerErrors) drawerErrors.textContent = '';

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState?.id;

    const sectionHTML = parsedState.sections?.['cart-drawer'];
    if (!sectionHTML) return;

    const newDoc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newDrawer = newDoc.querySelector('#CartDrawer');
    if (!newDrawer) return;

    const isEmpty = !!newDrawer.querySelector('cart-drawer-items.is-empty');
    if (isEmpty) {
      this.classList.add('is-empty');
      // The totals and the checkout button live outside the rowgroup, so
      // returning here left the last total on screen under an empty item list:
      // remove the only line and the drawer still read "Total $163 / Check out"
      // with nothing in it. Swap the whole inner so the empty state and the
      // footer land together.
      const oldInner = this.querySelector('.drawer__inner');
      const newInner = newDoc.querySelector('.drawer__inner');
      if (oldInner && newInner) oldInner.innerHTML = newInner.innerHTML;
      // after the swap, so the button being disabled is the one now on screen
      const emptyCheckout = this.querySelector('#CartDrawer-Checkout');
      if (emptyCheckout) emptyCheckout.setAttribute('disabled', 'disabled');
      return;
    }

    if (!window._CART_REQUESTS) {
      const oldRowgroup = this.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');
      const newRowgroup = newDrawer.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');
      if (oldRowgroup && newRowgroup) {
        oldRowgroup.querySelectorAll('.cart-item--optimistic, .cart-item--optimistic-gwp, .cart-item--skeleton').forEach(el => el.remove());
        oldRowgroup.innerHTML = newRowgroup.innerHTML;
        // Sync gift progress element immediately — SSR may have rendered is-active based on
        // cart.items_subtotal_price (which can diverge from the JS-computed optimistic total),
        // so override it synchronously before the 120ms debounce + /cart.js fetch corrects it.
        if (window.CartOptimist && typeof CartOptimist.syncGiftProgress === 'function') {
          CartOptimist.syncGiftProgress();
        }
      }

      const drawerItems = this.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.remove('is-empty');
    }

    const drawerContent = this.querySelector('.drawer__content');
    this.syncAdditionalProducts(drawerContent, newDrawer);

    const oldTotals = this.querySelector('.cartfrawerfooterWrap .cart-drawer__footer');
    const newTotals = newDrawer.querySelector('.cartfrawerfooterWrap .cart-drawer__footer');
    if (oldTotals && newTotals) {
      oldTotals.innerHTML = newTotals.innerHTML;
    }

    // GAH-AU-LAUNCH-01: the benefits row sits beside the footer, not in it, and
    // in Australia its shipping half depends on the cart (benchmark-au-free-ship)
    const oldBenefits = this.querySelector('.cartfrawerfooterWrap .fd-cart-benefits');
    const newBenefits = newDrawer.querySelector('.cartfrawerfooterWrap .fd-cart-benefits');
    if (oldBenefits && newBenefits && oldBenefits.innerHTML !== newBenefits.innerHTML) {
      oldBenefits.innerHTML = newBenefits.innerHTML;
    }

    // an add that requested sections/cart-instant-state carries the whole cart
    // (benchmark-pdp.js sets __fullCart); reconcile and the protection sync use
    // it instead of re-reading /cart.js
    const fullCart = parsedState.__fullCart || null;
    if (window.CartOptimist) CartOptimist.reconcile(fullCart || parsedState, { serverFooter: !!(oldTotals && newTotals) });

    const bubbleHTML = parsedState.sections?.['cart-icon-bubble'];
    if (bubbleHTML) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) bubbleEl.innerHTML = this.getSectionInnerHTML(bubbleHTML, '.shopify-section');
    }

    const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn) checkoutBtn.removeAttribute('disabled');

    if (window.ShippingProtection) {
      try {
        ShippingProtection.ensureWidget();
        ShippingProtection.hideCartItems();
        ShippingProtection.syncCart(fullCart || undefined);
      } catch(e) {}
    }

    this.reorderGwpItems();
    bmbFitUpgradeSoon();
  }

  syncAdditionalProducts(drawerContent, newDrawerRoot) {
    if (!drawerContent || !newDrawerRoot) return;

    drawerContent.querySelectorAll(':scope > .additional-product').forEach((block) => block.remove());

    const newAddonBlocks = Array.from(
      newDrawerRoot.querySelectorAll('.drawer__content > .additional-product')
    );

    newAddonBlocks.forEach((block) => {
      drawerContent.insertAdjacentHTML('beforeend', block.outerHTML);
    });

    const oldUpsell = drawerContent.querySelector(':scope > upsell-products:not(.cart-drawer__upsell--empty)');
    const newUpsell = newDrawerRoot.querySelector('.drawer__content > upsell-products:not(.cart-drawer__upsell--empty)');

    if (oldUpsell) oldUpsell.remove();
    if (newUpsell) drawerContent.insertAdjacentHTML('beforeend', newUpsell.outerHTML);
  }

  reorderGwpItems() {
    const rowgroup = this.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');
    if (!rowgroup) return;

    const rows = [...rowgroup.querySelectorAll(':scope > .cart-item')];
    if (!rows.length) return;

    const giftRows = rows.filter((row) => row.dataset.gwpParentId);
    const parentRows = rows.filter((row) => !row.dataset.gwpParentId && row.dataset.variantId);

    if (giftRows.length && parentRows.length) {
      parentRows.forEach((parentRow) => {
        const parentVariantId = String(parentRow.dataset.variantId || '');
        if (!parentVariantId) return;

        const relatedGifts = giftRows
          .filter((giftRow) => String(giftRow.dataset.gwpParentId || '') === parentVariantId)
          .sort((a, b) => {
            const aOrder = Number(a.dataset.gwpOrder || 999);
            const bOrder = Number(b.dataset.gwpOrder || 999);
            return aOrder - bOrder;
          });

        if (!relatedGifts.length) return;

        let anchor = parentRow;
        relatedGifts.forEach((giftRow) => {
          anchor.insertAdjacentElement('afterend', giftRow);
          anchor = giftRow;
        });
      });
    }

    // Consolidate fathers-day-offer--cart elements to after the first
    // instance of their parent product (by product handle)
    rowgroup.querySelectorAll('.fathers-day-offer--cart').forEach(fdo => {
      let prev = fdo.previousElementSibling;
      while (prev && !prev.classList.contains('cart-item')) {
        prev = prev.previousElementSibling;
      }
      const handle = prev?.dataset?.productHandle;
      if (!handle) return;

      const firstInstance = rowgroup.querySelector(`.cart-item[data-product-handle="${CSS.escape(handle)}"]`);
      if (!firstInstance || firstInstance === prev) return;

      let anchor = firstInstance;
      while (anchor.nextElementSibling && anchor.nextElementSibling !== fdo) {
        const next = anchor.nextElementSibling;
        const isGwpLike = next.classList.contains('cart-item--gwp') ||
                          next.classList.contains('fathers-day-offer--cart') ||
                          next.classList.contains('cart-item--cart-progress-gift');
        if (!isGwpLike) break;
        anchor = next;
      }
      anchor.after(fdo);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initUpsellProductsSlider() {
    const selectors = {
      slider: ".js-cart-upsell-products-slider",
      prev: ".js-cart-upsell-products-prev",
      next: ".js-cart-upsell-products-next",
    };

    const swiper = new Swiper(selectors.slider, {
      speed: 400,
      spaceBetween: 100,
      navigation: {
        nextEl: selectors.next,
        prevEl: selectors.prev
      }
    });
  }

  async fetchCartState() {
    const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to fetch cart state');
    return await response.json();
  }

  getRelatedGiftKeysForParent(cartState, parentItem) {
    if (!cartState?.items?.length || !parentItem?.key) return [];

    const parentVariantId = String(parentItem.id || parentItem.variant_id || '');
    const parentGroupId = parentItem?.properties?.['_gwp-group-id'];

    return cartState.items
      .filter((item) => {
        const isGift = item?.properties?.['_gwp-product'];
        if (!isGift) return false;

        const itemParentVariantId = String(item.properties['_gwp-product-for'] || '');
        const itemGroupId = item.properties['_gwp-group-id'];

        const byVariant = !parentGroupId && parentVariantId && itemParentVariantId === parentVariantId;
        const byGroup = parentGroupId && itemGroupId === parentGroupId;

        return byVariant || byGroup;
      })
      .map((item) => item.key)
      .filter(Boolean);
  }

  getUltimateUpgradeDataFromButton(button) {
    const fromKey = button.dataset.upgradeFromKey;
    const toVariantId = Number(button.dataset.upgradeToVariantId || 0);
    const requestedQuantity = Number(button.dataset.upgradeFromQuantity || 1);
    const upgradeTitle = button.dataset.upgradeToTitle || '';
    const giftVariantIds = (button.dataset.upgradeGiftVariantIds || '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
    const lessonGiftVariantIds = new Set(
      (button.dataset.upgradeGiftLessonVariantIds || '')
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => String(id))
    );
    const giftLessonsMap = (button.dataset.upgradeGiftLessonsMap || '')
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .reduce((acc, pair) => {
        const [variantIdRaw, countRaw] = pair.split(':');
        const variantId = (variantIdRaw || '').trim();
        const lessonsCount = (countRaw || '').trim();
        if (variantId && lessonsCount) acc[variantId] = lessonsCount;
        return acc;
      }, {});

    return {
      fromKey,
      toVariantId,
      requestedQuantity,
      upgradeTitle,
      giftVariantIds,
      lessonGiftVariantIds,
      giftLessonsMap,
    };
  }

  prepareUpgradePayload(cartState, upgradeData) {
    const fromItem = cartState.items.find((item) => item.key === upgradeData.fromKey);
    if (!fromItem) throw new Error('Original line item not found');

    const updates = { [fromItem.key]: 0 };
    this.getRelatedGiftKeysForParent(cartState, fromItem).forEach((giftKey) => {
      updates[giftKey] = 0;
    });

    const filteredProperties = {};
    const sourceProperties = fromItem.properties || {};
    const skippedPropertyKeys = new Set([
      '_ultimate-upgrade-to-variant-id',
      '_ultimate-upgrade-title',
      '_ultimate-upgrade-image',
      '_is-standard-edition',
      '_is-ultimate-edition',
      '_ultimate-edition-title',
      '_gwp-product-related',
      '_gwp-group-id',
    ]);

    Object.entries(sourceProperties).forEach(([key, value]) => {
      if (!key || skippedPropertyKeys.has(key)) return;
      filteredProperties[key] = value;
    });

    const normalizedQuantity = upgradeData.requestedQuantity > 0 ? upgradeData.requestedQuantity : 1;
    const gwpGroupId = `gwp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const mainLineProperties = {
      ...filteredProperties,
      '_is-ultimate-edition': 'true',
    };

    if (upgradeData.upgradeTitle) {
      mainLineProperties['_ultimate-edition-title'] = upgradeData.upgradeTitle;
    }

    const items = [];

    [...upgradeData.giftVariantIds].reverse().forEach((giftVariantId) => {
      const originalGiftOrder = upgradeData.giftVariantIds.indexOf(giftVariantId);
      const giftVariantIdString = String(giftVariantId);
      const giftLessonsCount = upgradeData.giftLessonsMap[giftVariantIdString];
      const isLessonGift = upgradeData.lessonGiftVariantIds.has(giftVariantIdString) || !!giftLessonsCount;
      const giftProperties = {
        '_gwp-product': 'true',
        '_gwp-product-for': String(upgradeData.toVariantId),
        '_gwp-group-id': gwpGroupId,
        '_gwp-order': String(originalGiftOrder > -1 ? originalGiftOrder : 999),
      };
      if (upgradeData.giftVariantIds.length > 1) {
        giftProperties['_gwp-ultimate'] = 'true';
      }

      if (isLessonGift) {
        giftProperties['_lessons'] = 'true';
      }

      if (giftLessonsCount) {
        giftProperties['_lessons_count'] = giftLessonsCount;
      }

      items.push({
        id: giftVariantId,
        quantity: normalizedQuantity,
        properties: giftProperties,
      });
    });

    items.push({
      id: upgradeData.toVariantId,
      quantity: normalizedQuantity,
      properties: mainLineProperties,
    });

    return {
      updates,
      addPayload: {
        items,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      },
    };
  }

  async executeCartUpgrade(payload) {
    const updateResponse = await fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ updates: payload.updates }),
    });
    if (!updateResponse.ok) throw new Error('Failed to remove original line item');

    const addResponse = await fetch(`${routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(payload.addPayload),
    });

    const parsedState = await addResponse.json();
    if (!addResponse.ok || parsedState.status) {
      throw new Error(parsedState.description || 'Failed to add upgraded item');
    }

    return parsedState;
  }

  async reconcileUpgradeCartState(parsedState) {
    let cartStateForOptimist = null;
    if (parsedState && Array.isArray(parsedState.items) && typeof parsedState.total_price === 'number') {
      cartStateForOptimist = parsedState;
    } else {
      try {
        cartStateForOptimist = await this.fetchCartState();
      } catch (stateError) {
        console.error('Failed to fetch cart state for optimistic reconcile:', stateError);
      }
    }

    if (cartStateForOptimist) {
      window._cartState = cartStateForOptimist;
      if (window.CartOptimist) CartOptimist.reconcile(cartStateForOptimist);
    }
  }

  applyUpgradeRenderState(parsedState) {
    this.renderContents(parsedState, 'add-to-cart-form');
    document.dispatchEvent(new CustomEvent("init:upsell-slider"));
    document.dispatchEvent(new CustomEvent("cart:reorder-addons"));

    if (typeof updateCartAndProductJSON === 'function') {
      updateCartAndProductJSON();
    }
  }

  resetUltimateUpgradeButtonState(button, spinner) {
    button.disabled = false;
    button.classList.remove('loading');
    if (spinner) spinner.classList.add('hidden');
  }

  async handleUltimateUpgradeClick(event) {
    const button = event.target.closest('[data-ultimate-upgrade-action]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();

    if (button.disabled || button.classList.contains('loading')) return;

    const upgradeData = this.getUltimateUpgradeDataFromButton(button);

    if (!upgradeData.fromKey || !upgradeData.toVariantId) return;

    const spinner = button.querySelector('.loading-overlay__spinner');
    button.disabled = true;
    button.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const cartState = await this.fetchCartState();
      const payload = this.prepareUpgradePayload(cartState, upgradeData);
      const parsedState = await this.executeCartUpgrade(payload);
      await this.reconcileUpgradeCartState(parsedState);
      this.applyUpgradeRenderState(parsedState);
    } catch (error) {
      if (window.CartOptimist) CartOptimist.revert();
      console.error('Ultimate upgrade failed:', error);
    } finally {
      this.resetUltimateUpgradeButtonState(button, spinner);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  smoothSectionRender(parsedState) {
    const cartDrawer = document.querySelector('cart-drawer');
    const scrollContainer = cartDrawer?.querySelector('.drawer__inner');
    const scrollTop = scrollContainer?.scrollTop || 0;

    const sectionHTML = parsedState.sections?.['cart-drawer'];
    if (!sectionHTML || isCartEffectivelyEmpty(parsedState)) {
      super.smoothSectionRender(parsedState);
      // `super.smoothSectionRender` wholesale-replaces `.drawer__inner` innerHTML,
      // which strips any classes/positions reorderGwpItems applied (e.g. the
      // master row's `cart-item--gwp-related` and the progress-gift placement).
      // This branch is also hit by `initCartDrawerDefaultState`, whose payload
      // has no `items` array — so the new rowgroup may actually contain items
      // and need reordering.
      if (cartDrawer && typeof cartDrawer.reorderGwpItems === 'function') {
        cartDrawer.reorderGwpItems();
      }
      return;
    }

    const newDoc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newDrawer = newDoc.querySelector('.drawer__inner');
    if (!newDrawer) {
      super.smoothSectionRender(parsedState);
      return;
    }

    const oldRowgroup = cartDrawer.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');
    const newRowgroup = newDrawer.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');
    if (oldRowgroup && newRowgroup) {
      oldRowgroup.querySelectorAll('.cart-item--optimistic, .cart-item--optimistic-gwp, .cart-item--skeleton').forEach(el => el.remove());
      oldRowgroup.innerHTML = newRowgroup.innerHTML;
    }

    const drawerContent = cartDrawer.querySelector('.drawer__content');
    if (cartDrawer && typeof cartDrawer.syncAdditionalProducts === 'function') {
      cartDrawer.syncAdditionalProducts(drawerContent, newDrawer);
    }

    const oldTotals = cartDrawer.querySelector('.cartfrawerfooterWrap .cart-drawer__footer');
    const newTotals = newDrawer.querySelector('.cartfrawerfooterWrap .cart-drawer__footer');
    if (oldTotals && newTotals) {
      oldTotals.innerHTML = newTotals.innerHTML;
    }

    // GAH-AU-LAUNCH-01: same benefits-row sync as renderContents
    const oldBenefits = cartDrawer.querySelector('.cartfrawerfooterWrap .fd-cart-benefits');
    const newBenefits = newDrawer.querySelector('.cartfrawerfooterWrap .fd-cart-benefits');
    if (oldBenefits && newBenefits && oldBenefits.innerHTML !== newBenefits.innerHTML) {
      oldBenefits.innerHTML = newBenefits.innerHTML;
    }

    if (window.CartOptimist) CartOptimist.reconcile(parsedState, { serverFooter: !!(oldTotals && newTotals) });

    const bubbleSection = this.getSectionsToRender().find(s => s.section === 'cart-icon-bubble');
    if (bubbleSection && parsedState.sections[bubbleSection.section]) {
      const bubbleEl = document.getElementById(bubbleSection.id);
      if (bubbleEl) bubbleEl.innerHTML = this.getSectionInnerHTML(parsedState.sections[bubbleSection.section], bubbleSection.selector);
    }

    cartDrawer.classList.toggle('is-empty', isCartEffectivelyEmpty(parsedState));

    const checkoutBtn = cartDrawer.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn && !isCartEffectivelyEmpty(parsedState)) checkoutBtn.removeAttribute('disabled');

    if (window.ShippingProtection) {
      try {
        ShippingProtection.ensureWidget();
        ShippingProtection.hideCartItems();
      } catch(e) {}
    }

    if (cartDrawer && typeof cartDrawer.reorderGwpItems === 'function') {
      cartDrawer.reorderGwpItems();
    }

    if (scrollContainer) {
      requestAnimationFrame(() => { scrollContainer.scrollTop = scrollTop; });
    }
    bmbFitUpgradeSoon();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
